<template lang="pug">
div.commentary-group-outer
  ErrorAlert(
    v-if="headingError"
    :reporting="{ message: headingError.message, extra: headingError }"
    :message="headingError.message"
    :stack="headingError.stack"
    @retry="headingQuery.refetch"
  )
  h2.section-title-2(
    v-else
    v-html="heading ?? '…'"
  )

  ErrorAlert(
    v-if="verseError"
    :reporting="{ message: verseError.message, extra: verseError }"
    :message="verseError.message"
    :stack="verseError.stack"
    @retry="verseQuery.refetch"
  )
  VvBlockQuote(v-else-if="verseQuote || verseQuery.isPending.value")
    VSkeletonLoader(
      v-if="verseQuery.isPending.value"
      type="paragraph"
    )
    p.text-body-1(
      v-else
      v-html="verseQuote"
    )

  div.commentary-doc.my-2(
    v-for="doc in docs"
    :key="doc.nid"
  )
    CommentTeaserCard(
      v-if="doc.comment"
      :heading="doc.comment.heading"
      :teaser="doc.comment.teaser"
      :ranges="doc.comment.ranges"
      :detail-level-name="doc.comment.detailLevel.name[locale] ?? '?'"
      :detail-level-num-dots="numDotsPerDetailLevelWeight[doc.comment.detailLevel.weight] ?? 0"
      :commentary-type-name="commentaryTypeName"
      :commentary-type-short-name="commentaryTypeShortName"
      :nid="doc.nid"
      :local-tid="localTid"
      :is-selected="selected.includes(doc.nid)"
      :show-default-reading-list="true"
      :show-reading-list-menu="true"
      :language="doc.comment.language"
    )
    CommentaryImage(
      v-else-if="doc.image"
      :title="doc.image.title"
      :description="doc.image.description"
      :large-url="doc.image.styles.large"
      :small-url="doc.image.styles.small"
      :placeholder-data="doc.image.styles.placeholder"
      :original-url="doc.image.styles.original"
    )
  div.hr(v-if="subGroupComputed.subGroups.length > 0 && verseQuote")

  CommentaryGroupInner(
    v-for="group in subGroupComputed.subGroups"
    :key="group.versePart || '?'"
    :range="group.range"
    :verse="group.verse"
    :verse-part="group.versePart"
    :doclist="group.doclist"
    :local-tid="localTid"
    :commentary-type-name="commentaryTypeName"
    :commentary-type-short-name="commentaryTypeShortName"
    :selected="selected"
    :translations-by-nid="subGroupComputed.otherCommentsByNid"
  )
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { okAsync } from "neverthrow";
import { useQuery } from "@tanstack/vue-query";
import type { StandaloneVerseData } from "@rsc/scripture-model";
import { createVerse } from "@rsc/scripture-util";
import { injectRequired } from "~/injectRequired";
import { LoggerKey, ScriptureClientKey } from "~/injectionKeys";
import { numDotsPerDetailLevelWeight } from "~/static/taxonomy/detailLevels";
import type { Props } from "~/components/CommentaryGroupOuter.types";
import {
  logSsrErrorToSentry,
  makeResultAsyncSerializable,
  type SerializableErrorInfo,
} from "~/errors/util";
import {
  computeDocList,
  computeSubGroups,
  type ComputedSubGroups,
  type DocComputed,
} from "./CommentaryGroup.util";

const i18n = useI18n();
const locale = i18n.locale;

const scriptureClient = injectRequired(ScriptureClientKey);

const logger = injectRequired(LoggerKey);

const props = withDefaults(defineProps<Props>(), {
  subGroups: () => [],
  selected: () => [],
});

const verseQuery = useQuery({
  queryKey: [
    "CommentaryGroupOuter",
    "verse",
    computed(() => props.range?.dump()),
    computed(() => props.localTid),
  ],
  queryFn: () =>
    makeResultAsyncSerializable(
      () => {
        if (!props.range) {
          // We do not have a specific verse. There is probably an explicit heading.
          return okAsync(undefined);
        }

        if (props.range.single) {
          // This is an outer group for a single verse.
          return props.range
            .changeTranslation(props.range?.tid || props.localTid)
            .getFirstVerse(scriptureClient)
            .map((data) => data ?? undefined);
        }

        // This is an outer group for a range of verses (or a compound verse).
        return okAsync(undefined);
      },
      (e, s) => logSsrErrorToSentry(logger, e, s),
    ),
});

const verseData = computed<StandaloneVerseData | undefined>(
  () => verseQuery.data.value?.value,
);

const verseError = computed<Error | SerializableErrorInfo | undefined>(
  () => verseQuery.error.value ?? verseQuery.data.value?.error,
);

const verseQuote = computed<string>(() =>
  verseData.value ? createVerse(verseData.value).getFormattedText() : "",
);

const headingQuery = useQuery({
  queryKey: [
    "CommentaryGroupOuter",
    "heading",
    computed(() => props.heading),
    computed(() => props.range?.dump()),
    computed(() => props.localTid),
  ],
  queryFn: () =>
    makeResultAsyncSerializable(
      () => {
        if (props.heading) {
          return okAsync(props.heading);
        }

        if (props.range) {
          return props.range.renderReference(
            scriptureClient,
            props.localTid,
            !!props.range.tid,
            false,
          );
        }

        return okAsync("General");
      },
      (e, s) => logSsrErrorToSentry(logger, e, s),
    ),
});

const heading = computed<string | undefined>(
  () => headingQuery.data.value?.value,
);

const headingError = computed<Error | SerializableErrorInfo | undefined>(
  () => headingQuery.error.value ?? headingQuery.data.value?.error,
);

const docs = computed<DocComputed[]>(() => {
  return computeDocList(props.doclist.docs);
});

const subGroupComputed = computed<ComputedSubGroups>(() => {
  return computeSubGroups(toRaw(props.subGroups));
});
</script>
